<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useField, useForm } from 'vee-validate';
import { format, isSameDay, startOfMonth, addMonths, subMonths, startOfWeek, addDays, isSameMonth, isToday } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { getLocalizedText, useResponsiveSize } from '../utils/formHelpers';
import type { FormField } from '../types/formTypeStructure';
import FormFieldWrapper from '../FormFieldWrapper.vue';
import { AlertCircle } from "lucide-vue-next";

const props = defineProps<{
  field: FormField & { fieldType: 'date' },
  name?: string,
  currentLang?: string,
  defaultValue?: string,
  className?: string,
}>();

const emit = defineEmits(['update:modelValue']);

const fieldName = props.name ?? props.field.fieldName.toString();
const currentLang = props.currentLang ?? 'fr';
const label = computed(() => getLocalizedText(props.field.label, currentLang))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const { value, errorMessage, handleChange } = useField<string>(fieldName);
const { meta } = useForm();

const isOpen = ref(false);
const containerRef = ref<HTMLElement | null>(null);

const selectedDate = ref<Date | null>(props.defaultValue ? new Date(props.defaultValue) : null);
const currentMonth = ref(new Date());

const months = computed(() => currentLang === 'fr'
  ? ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
  : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']);

const daysOfWeek = computed(() => currentLang === 'fr'
  ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
  : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);

const toggleCalendar = () => {
  isOpen.value = !isOpen.value;
};

const formatDate = (date: Date | null) => {
  if (!date) return '';
  return format(date, 'PPP', { locale: currentLang === 'fr' ? fr : enUS });
};

const handleDateSelect = (date: Date) => {
  selectedDate.value = date;
  handleChange(date.toISOString().split('T')[0]);
  isOpen.value = false;
};

// Fonction manquante ajoutée
const isSelected = (date: Date) => {
  return selectedDate.value && isSameDay(date, selectedDate.value);
};

const daysInMonth = computed(() => {
  const start = startOfWeek(startOfMonth(currentMonth.value), { weekStartsOn: 0 });
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
});

const navigateMonth = (dir: 'next' | 'prev') => {
  currentMonth.value = dir === 'next' ? addMonths(currentMonth.value, 1) : subMonths(currentMonth.value, 1);
};

const handleClickOutside = (e: MouseEvent) => {
  if (containerRef.value && !containerRef.value.contains(e.target as Node)) {
    isOpen.value = false;
  }
};

onMounted(() => document.addEventListener('mousedown', handleClickOutside));
onUnmounted(() => document.removeEventListener('mousedown', handleClickOutside));

const screenWidth = ref(window.innerWidth);

const size = computed(() => calculateSize(screenWidth.value))

const updateScreenWidth = () => {
  screenWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', updateScreenWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenWidth)
})

// Exemple d'utilisation dans un style :
const inputStyles = computed(() => ({
  width: `${size.value.width}px`,
  height: `${size.value.height}px`,
  borderRadius: `${size.value.borderRadius}px`,
}))

function calculateSize(screenWidth: number): { width: number; height: number; borderRadius: number } {
  let width: number;
  let height: number;

  // Width calculation based on breakpoints (same logic as useResponsiveSize)
  if (screenWidth <= 320) width = 250;          // mobile-s
  else if (screenWidth <= 375) width = 310;     // mobile-m
  else if (screenWidth <= 425) width = 330;     // mobile-l
  else if (screenWidth <= 768) width = 300;     // tablet
  else if (screenWidth <= 1024) width = 300;    // laptop
  else if (screenWidth <= 1440) width = 380;    // laptop-l
  else if (screenWidth <= 1920) width = 420;    // desktop
  else if (screenWidth <= 2560) width = 420;    // 4k-screen
  else width = 500;

  // Height calculation (linear interpolation between mobile-s and 4k-screen)
  const heightMobileS = 35;
  const heightKScreen = 65;
  const heightSlope = (heightKScreen - heightMobileS) / (2560 - 320);
  
  height = heightSlope * (screenWidth - 320) + heightMobileS;
  height = Math.max(heightMobileS, Math.min(heightKScreen, height));

  // Border radius calculation (25% of height, clamped between 8 and 16)
  const radiusRatio = 0.25;
  const borderRadius = Math.round(height * radiusRatio);
  const finalBorderRadius = Math.max(8, Math.min(16, borderRadius));

  return {
    width: Math.round(width),
    height: Math.round(height),
    borderRadius: finalBorderRadius,
  };
}
</script>

<template>
  <div class="date-field-container">
    <!-- Label dupliqué au-dessus du sélecteur de date -->
    <div class="date-field-label-container">
      <span class="date-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div ref="containerRef" class="relative mb-4">
        <input type="hidden" :name="fieldName" :value="value" />

        <button
          type="button"
          @click="toggleCalendar"
          class="flex items-center justify-between px-4 py-2 border transition-all group hover:shadow-lg"
          :style="{
            ...inputStyles,
            borderColor: isOpen ? '#033a5a' : '#04406E',
            background: isOpen ? '#e6f0f5' : '#fff',
            fontWeight: '500',
            color: selectedDate ? '#04406E' : '#999'
          }"
        >
          <span class="flex items-center gap-2">
            <svg class="opacity-70" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{ selectedDate ? formatDate(selectedDate) : (currentLang === 'fr' ? 'Sélectionner une date' : 'Select a date') }}
          </span>
          <svg :class="isOpen ? 'rotate-180' : ''" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6,9 12,15 18,9" />
          </svg>
        </button>

        <transition name="fade">
          <div
            v-if="isOpen"
            class="absolute z-2000 bg-white border rounded-xl shadow-xl mt-2"
          >
            <div class="flex justify-between items-center p-2 bg-[#e6f0f5]">
              <button @click="navigateMonth('prev')" class="text-[#04406E] hover:bg-white p-1 rounded">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="15,18 9,12 15,6" />
                </svg>
              </button>
              <div class="font-semibold text-[#04406E]">
                {{ months[currentMonth.getMonth()] }} {{ currentMonth.getFullYear() }}
              </div>
              <button @click="navigateMonth('next')" class="text-[#04406E] hover:bg-white p-1 rounded">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="9,18 15,12 9,6" />
                </svg>
              </button>
            </div>

            <div class="grid grid-cols-7 text-center text-sm text-gray-600 font-medium p-2">
              <div v-for="day in daysOfWeek" :key="day">{{ day }}</div>
            </div>

            <div class="grid grid-cols-7 gap-0 p-2">
              <button
                v-for="(day, i) in daysInMonth"
                :key="i"
                @click="handleDateSelect(day)"
                class="text-sm p-2 rounded-lg hover:scale-105 transition"
                :class="{
                  'text-gray-400': !isSameMonth(day, currentMonth),
                  'text-gray-700 hover:bg-gray-100': isSameMonth(day, currentMonth) && !isSelected(day) && !isToday(day),
                  'bg-blue-500 text-white': isToday(day) && !isSelected(day),
                  'ring-2 ring-blue-300': isToday(day) && !isSelected(day),
                  'text-white font-bold': isSelected(day),
                }"
                :style="{
                  backgroundColor: isSelected(day) ? '#04406E' : '',
                  color: isSelected(day) ? 'white' : ''
                }"
              >
                {{ day.getDate() }}
              </button>
            </div>

            <div class="border-t p-3">
              <button @click="handleDateSelect(new Date())" class="w-full py-2 text-sm text-[#04406E] font-medium hover:bg-gray-100">
                {{ currentLang === 'fr' ? 'Aujourd\'hui' : 'Today' }}
              </button>
            </div>
          </div>
        </transition>

        <p v-if="errorMessage" class="text-red-500 text-sm mt-1">{{ errorMessage }}</p>
      </div>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.date-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.date-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.date-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.2s ease-out;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>